import { ref, watch } from 'vue';

export type ThemeId = 'arena-dark' | 'light' ;

export interface ThemeOption {
  id: ThemeId;
  label: string;
  description: string;
  icon: string;
  preview: { bg: string; surface: string; accent: string; text: string };
}

export const THEMES: ThemeOption[] = [
  {
    id: 'light',
    label: 'Classic Light',
    description: 'Clean white interface',
    icon: 'pi pi-sun',
    preview: { bg: '#ffffff', surface: '#f9fafb', accent: '#2563eb', text: '#1f2937' },
  },
  {
    id: 'arena-dark',
    label: 'Arena Dark',
    description: 'Dark slate, inspired by live events',
    icon: 'pi pi-moon',
    preview: { bg: '#020617', surface: '#0f172a', accent: '#3b82f6', text: '#f8fafc' },
  },
];

const STORAGE_KEY = 'app-theme';

// Shared reactive state (singleton across all composable instances)
const currentTheme = ref<ThemeId>('arena-dark');
let initialized = false;

function applyTheme(theme: ThemeId) {
  document.documentElement.setAttribute('data-theme', theme);
  // PrimeVue v4 resolves its own light/dark tokens via `darkModeSelector`
  // (configured to `.p-dark` in main.ts). Without this, PrimeVue followed the
  // OS colour scheme while the app tokens followed data-theme — the two could
  // disagree, producing mixed light/dark surfaces (Trello #158).
  document.documentElement.classList.toggle('p-dark', theme !== 'light');
}

function loadTheme(): ThemeId {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && THEMES.some((t) => t.id === stored)) {
      return stored as ThemeId;
    }
  } catch {
    // localStorage might not be available
  }
  return 'arena-dark';
}

function saveTheme(theme: ThemeId) {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // ignore
  }
}

export function useTheme() {
  if (!initialized) {
    initialized = true;
    currentTheme.value = loadTheme();
    applyTheme(currentTheme.value);
  }

  watch(currentTheme, (newTheme) => {
    applyTheme(newTheme);
    saveTheme(newTheme);
  });

  const setTheme = (theme: ThemeId) => {
    currentTheme.value = theme;
  };

  const toggleTheme = () => {
    currentTheme.value = currentTheme.value === 'light' ? 'arena-dark' : 'light';
  };

  return {
    currentTheme,
    themes: THEMES,
    setTheme,
    toggleTheme,
  };
}
